/**
 * React Query mutation hook for creating a new component rule.
 *
 * Fired from the "Save as draft" button inside the preview modal on the
 * component config page. The mutation invalidates every rule list under
 * the target component id so the newly-created row shows up immediately.
 */

import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import {
  componentRulesService,
  type ComponentRuleDTO,
  type CreateComponentRulePayload,
} from '../../api/componentRulesService';
import { componentRulesKeys } from './queryKeys';

/**
 * Mutation hook for `POST /claimflow/component-rules`.
 *
 * Returns the standard `UseMutationResult` so callers get `mutate`,
 * `mutateAsync`, `isPending`, error state, and reset in one object.
 * Invalidates every list query under the target `component_id` on
 * success; detail queries are left untouched because there is nothing to
 * freshen (the detail of the new row is the mutation response itself).
 */
export function useCreateComponentRule(): UseMutationResult<
  ComponentRuleDTO,
  Error,
  CreateComponentRulePayload
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateComponentRulePayload) =>
      componentRulesService.createRule(payload),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: componentRulesKeys.list(variables.component_id),
      });
      void queryClient.invalidateQueries({
        queryKey: componentRulesKeys.lists(),
      });
    },
  });
}
